import * as React from "react";
import * as ReactDOM from "react-dom";
import clsx from "clsx";
import {
  HeartPulse,
  FlaskConical,
  FileText,
  RefreshCw,
  Check,
  X,
  Minus,
  Play,
  ChevronDown,
  Rocket,
  Square,
} from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { Callout } from "../../../ui/components/Callout";
import { Button } from "../../../ui/components/Button";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { ListView } from "../../../ui/components/ListView";
import { ListViewCard } from "../../../ui/components/ListView/components/ListViewCard";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { ListParams } from "../../../ui/api/listApi.types";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import { useAuth } from "../../lib/auth/hooks";
import { getAuthCookie } from "../../lib/auth/session-cookie";
import { useWebSocket } from "../../hooks/useWebSocket";
import { useI18n } from "../../lib/i18n";
import {
  fetchE2eHealth,
  startE2eRun,
  stopE2eRun,
  type E2eHealthReport,
  type E2ePlaywrightProject,
  type E2eRun,
  type E2eRunProjectSegment,
  type E2eRunStatus,
  type E2eSpecResult,
} from "../../lib/api/e2e";
import { ProApiClientError } from "../../lib/api/pro-api.client";
import * as riStyles from "../../../ui/components/RealtimeIndicator/RealtimeIndicator.style.css";
import * as s from "./E2eTestingPage.css";

const SCOPE = "system-e2e";
const SECTION_HEALTH = "e2e-health";
const SECTION_RUNNER = "e2e-runner";
const SECTION_REPORT = "e2e-report";

const RING_SIZE = 72;
const STROKE = 8;
const RADIUS = (RING_SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const RUNNER_RING_SIZE = 68;
const RUNNER_STROKE = 7;
const RUNNER_RADIUS = (RUNNER_RING_SIZE - RUNNER_STROKE) / 2;
const RUNNER_CIRCUMFERENCE = 2 * Math.PI * RUNNER_RADIUS;
/** Client-side fallback while prepareProgress has not arrived yet (~2.5m). */
const PREPARE_ESTIMATE_MS = 150_000;

const COLOR_PASSED = "#16A34A";
const COLOR_FAILED = "#DC2626";
const COLOR_SKIPPED = "#FFFFFF";
const COLOR_PENDING = "rgba(156,163,175,0.45)";

const E2E_PROJECTS: E2ePlaywrightProject[] = [
  "smoke",
  "authenticated",
  "onboarding",
];

type SpecBucket = "passed" | "failed" | "skipped";

type ProjectTotals = {
  project: string;
  passed: number;
  failed: number;
  skipped: number;
  total: number;
};

type SpecRow = E2eSpecResult & { id: string };

function pct(part: number, total: number): number {
  if (total <= 0) return 0;
  return (part / total) * 100;
}

function formatPct(part: number, total: number): string {
  return `${pct(part, total).toFixed(1)}%`;
}

function formatDuration(ms: number): string {
  if (!ms || ms < 0) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const sec = ms / 1000;
  if (sec < 60) return `${sec.toFixed(1)}s`;
  const min = Math.floor(sec / 60);
  const rem = Math.round(sec % 60);
  return `${min}m ${rem}s`;
}

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatRelativeAgo(iso: string | null): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "—";
  const delta = Math.max(0, Date.now() - then);
  const sec = Math.round(delta / 1000);
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.round(min / 60);
  if (hr < 48) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.round(hr / 24);
  return `${day} day${day === 1 ? "" : "s"} ago`;
}

const COLOR_AWS = "#64748B";
const COLOR_PUBLISH = "#D97706";
const COLOR_PLAYWRIGHT_BLOB = "#16A34A";
const COLOR_SETUP = "#0891B2";
/** Playwright config / dependency order for duration segments. */
const PROJECT_DURATION_ORDER = [
  "smoke",
  "setup",
  "authenticated",
  "onboarding",
] as const;
const PROJECT_SEGMENT_COLORS: Record<string, string> = {
  smoke: "#2563EB",
  setup: COLOR_SETUP,
  authenticated: "#7C3AED",
  onboarding: "#DB2777",
};

type DurationSegment = {
  id: string;
  label: string;
  ms: number;
  color: string;
};

function buildDurationSegments(report: E2eHealthReport): DurationSegment[] {
  const out: DurationSegment[] = [];
  const segs = report.segments;
  const projects = (segs?.projects ?? []).filter((p) => p.durationMs > 0);
  const projectsSum = projects.reduce((sum, p) => sum + p.durationMs, 0);
  const playwrightMs =
    (segs?.playwrightMs && segs.playwrightMs > 0
      ? segs.playwrightMs
      : null) ??
    (typeof report.playwrightDurationMs === "number" &&
    report.playwrightDurationMs > 0
      ? report.playwrightDurationMs
      : projectsSum);

  let awsMs = segs?.awsMs && segs.awsMs > 0 ? segs.awsMs : 0;
  let publishMs = segs?.publishMs && segs.publishMs > 0 ? segs.publishMs : 0;

  // Older summaries omitted prepare/publish — attribute leftover wall clock to prepare.
  if (!awsMs && report.durationMs > 0) {
    const accounted =
      (projects.length > 0 ? projectsSum : playwrightMs) + publishMs;
    const gap = report.durationMs - accounted;
    if (gap >= 1000) awsMs = gap;
  }

  if (awsMs > 0) {
    out.push({
      id: "aws",
      label: "Prepare",
      ms: awsMs,
      color: COLOR_AWS,
    });
  }

  if (projects.length) {
    const byName = new Map(projects.map((p) => [p.name, p] as const));
    const orderedNames = [
      ...PROJECT_DURATION_ORDER.filter((name) => byName.has(name)),
      ...projects
        .map((p) => p.name)
        .filter((name) => !(PROJECT_DURATION_ORDER as readonly string[]).includes(name)),
    ];
    const seen = new Set<string>();
    for (const name of orderedNames) {
      if (seen.has(name)) continue;
      seen.add(name);
      const p = byName.get(name);
      if (!p) continue;
      out.push({
        id: `project-${p.name}`,
        label: p.name,
        ms: p.durationMs,
        color: PROJECT_SEGMENT_COLORS[p.name] ?? COLOR_SETUP,
      });
    }
  } else if (playwrightMs > 0) {
    out.push({
      id: "playwright",
      label: "Playwright",
      ms: playwrightMs,
      color: COLOR_PLAYWRIGHT_BLOB,
    });
  }

  if (publishMs > 0) {
    out.push({
      id: "publish",
      label: "Publish",
      ms: publishMs,
      color: COLOR_PUBLISH,
    });
  }

  if (!out.length && report.durationMs > 0) {
    out.push({
      id: "total",
      label: "Total",
      ms: report.durationMs,
      color: COLOR_PLAYWRIGHT_BLOB,
    });
  }
  return out;
}

function shortSha(sha: string): string {
  if (!sha) return "—";
  return sha.slice(0, 7);
}

/** Only allow http(s) run links — avoids browser file:/// security errors. */
function safeHttpUrl(url: string | undefined | null): string | null {
  const raw = (url ?? "").trim();
  if (!raw) return null;
  try {
    const parsed = new URL(raw);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") return raw;
  } catch {
    /* ignore */
  }
  return null;
}

function bucketStatus(status: E2eSpecResult["status"]): SpecBucket {
  if (status === "passed") return "passed";
  if (status === "skipped") return "skipped";
  return "failed";
}

function formatSpecPathBadge(file: string): string {
  return file.replace(/\\/g, "/").toUpperCase();
}

function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
}

function aggregateByProject(specs: E2eSpecResult[]): ProjectTotals[] {
  const map = new Map<string, ProjectTotals>();
  for (const spec of specs) {
    const key = spec.project || "unknown";
    let row = map.get(key);
    if (!row) {
      row = { project: key, passed: 0, failed: 0, skipped: 0, total: 0 };
      map.set(key, row);
    }
    const bucket = bucketStatus(spec.status);
    row[bucket] += 1;
    row.total += 1;
  }
  return [...map.values()].sort((a, b) => a.project.localeCompare(b.project));
}

function conclusionStatusColors(
  conclusion: E2eHealthReport["conclusion"],
): (typeof riStyles.STATUS_COLORS)[keyof typeof riStyles.STATUS_COLORS] {
  if (conclusion === "passed") return riStyles.STATUS_COLORS.online;
  if (conclusion === "failed") return riStyles.STATUS_COLORS.offline;
  if (conclusion === "cancelled") return riStyles.STATUS_COLORS.stale;
  return riStyles.STATUS_COLORS.unknown;
}

function isTerminalRunStatus(status: E2eRunStatus): boolean {
  return (
    status === "succeeded" ||
    status === "failed" ||
    status === "stopped" ||
    status === "timed_out" ||
    status === "fault"
  );
}

function isActiveRunStatus(status: E2eRunStatus | undefined): boolean {
  if (!status) return false;
  return !isTerminalRunStatus(status);
}

function LastRunBadge({
  report,
  runUrl,
}: {
  report: E2eHealthReport;
  runUrl: string | null;
}) {
  const { t } = useI18n();
  const wrapperRef = React.useRef<HTMLSpanElement>(null);
  const [tooltipVisible, setTooltipVisible] = React.useState(false);
  const [tooltipPos, setTooltipPos] = React.useState<{
    bottom: number;
    left: number;
  } | null>(null);
  const colors = conclusionStatusColors(report.conclusion);
  const clickable = Boolean(runUrl);
  const isCodeBuild =
    Boolean(runUrl?.includes("codesuite/codebuild")) ||
    Boolean(report.github.runUrl?.includes("codesuite/codebuild"));

  const showTooltip = React.useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setTooltipPos({
        bottom: window.innerHeight - rect.top + 8,
        left: rect.left + rect.width / 2,
      });
    }
    setTooltipVisible(true);
  }, []);
  const hideTooltip = React.useCallback(() => setTooltipVisible(false), []);

  const openRun = React.useCallback(() => {
    if (!runUrl) return;
    hideTooltip();
    window.open(runUrl, "_blank", "noopener,noreferrer");
  }, [runUrl, hideTooltip]);

  const githubLine = report.github.branch
    ? `${report.github.branch}@${shortSha(report.github.sha)}`
    : report.github.runId
      ? `run #${report.github.runId}`
      : null;

  const tooltipEl =
    tooltipVisible && tooltipPos ? (
      <div
        className={riStyles.tooltipPortal}
        style={{
          position: "fixed",
          bottom: tooltipPos.bottom,
          left: tooltipPos.left,
          transform: "translateX(-50%)",
        }}
        role="tooltip"
      >
        <div className={riStyles.tooltipInner} style={{ position: "relative" }}>
          <span className={riStyles.tooltipTitle}>{t("pro.system.e2e.lastRun")}</span>
          <span className={riStyles.tooltipRow}>
            Environment: {report.environment}
          </span>
          <span className={riStyles.tooltipRow}>
            When: {formatWhen(report.generatedAt)}
          </span>
          <span className={riStyles.tooltipRow}>
            Duration: {formatDuration(report.durationMs)}
          </span>
          {typeof report.playwrightDurationMs === "number" &&
          report.playwrightDurationMs > 0 &&
          report.playwrightDurationMs !== report.durationMs ? (
            <span className={riStyles.tooltipRow}>
              Playwright: {formatDuration(report.playwrightDurationMs)}
            </span>
          ) : null}
          {report.segments?.awsMs ? (
            <span className={riStyles.tooltipRow}>
              Prepare: {formatDuration(report.segments.awsMs)}
            </span>
          ) : null}
          {report.segments?.publishMs ? (
            <span className={riStyles.tooltipRow}>
              Publish: {formatDuration(report.segments.publishMs)}
            </span>
          ) : null}
          {(report.segments?.projects ?? []).map((p) => (
            <span key={p.name} className={riStyles.tooltipRow}>
              {p.name}: {formatDuration(p.durationMs)}
            </span>
          ))}
          {githubLine ? (
            <span className={riStyles.tooltipRow}>
              {isCodeBuild ? "Build" : "GitHub"}: {githubLine}
            </span>
          ) : null}
          {report.github.runId ? (
            <span className={riStyles.tooltipRow}>
              Run ID: {report.github.runId}
            </span>
          ) : null}
          {clickable ? (
            <span className={riStyles.tooltipDescription}>
              Click to open the {isCodeBuild ? "CodeBuild" : "CI"} run
            </span>
          ) : null}
          <div className={riStyles.tooltipCaret} />
        </div>
      </div>
    ) : null;

  return (
    <>
      <span
        ref={wrapperRef}
        className={riStyles.badge}
        style={{
          background: colors.background,
          color: colors.color,
          borderColor: colors.borderColor,
          cursor: clickable ? "pointer" : "default",
        }}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onClick={clickable ? openRun : undefined}
        role={clickable ? "link" : undefined}
        tabIndex={clickable ? 0 : undefined}
        onKeyDown={
          clickable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openRun();
                }
              }
            : undefined
        }
        aria-label={t("pro.system.e2e.lastRunDetails")}
      >
        <span className={riStyles.iconSlot}>
          <svg width={8} height={8} viewBox="0 0 8 8" aria-hidden="true">
            <circle cx={4} cy={4} r={4} fill={colors.ring} />
          </svg>
        </span>
        Last Run
      </span>
      {typeof document !== "undefined" &&
        ReactDOM.createPortal(tooltipEl, document.body)}
    </>
  );
}

function ProjectRing({
  passed,
  failed,
  skipped,
  total,
}: {
  passed: number;
  failed: number;
  skipped: number;
  total: number;
}) {
  const pLen = total > 0 ? (passed / total) * CIRCUMFERENCE : 0;
  const fLen = total > 0 ? (failed / total) * CIRCUMFERENCE : 0;
  const sLen = total > 0 ? (skipped / total) * CIRCUMFERENCE : 0;

  let offset = 0;
  const segments: Array<{ len: number; color: string }> = [
    { len: pLen, color: COLOR_PASSED },
    { len: fLen, color: COLOR_FAILED },
    { len: sLen, color: COLOR_SKIPPED },
  ];

  return (
    <div className={s.ringWrap}>
      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        aria-hidden
      >
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="rgba(156,163,175,0.25)"
          strokeWidth={STROKE}
        />
        {segments.map((seg, i) => {
          if (seg.len <= 0) return null;
          const dashoffset = -offset;
          offset += seg.len;
          return (
            <circle
              key={i}
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={seg.color}
              strokeWidth={STROKE}
              strokeDasharray={`${seg.len} ${CIRCUMFERENCE - seg.len}`}
              strokeDashoffset={dashoffset}
              strokeLinecap="butt"
              transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
              style={
                seg.color === COLOR_SKIPPED
                  ? { filter: "drop-shadow(0 0 0.5px rgba(0,0,0,0.35))" }
                  : undefined
              }
            />
          );
        })}
      </svg>
      <span className={s.ringPct}>{formatPct(passed, total)}</span>
    </div>
  );
}

function useSmoothNumber(target: number, durationMs = 450): number {
  const [value, setValue] = React.useState(target);
  const valueRef = React.useRef(target);
  React.useEffect(() => {
    const from = valueRef.current;
    const to = target;
    if (Math.abs(from - to) < 0.0005) {
      valueRef.current = to;
      setValue(to);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - (1 - t) ** 3;
      const next = from + (to - from) * eased;
      valueRef.current = next;
      setValue(next);
      if (t < 1) raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value;
}

function RunnerProgressRing({
  progress,
  status,
}: {
  progress: number;
  status?: E2eRunProjectSegment["status"];
}) {
  const clampedTarget = Math.max(0, Math.min(1, progress));
  const smooth = useSmoothNumber(clampedTarget);
  const len = smooth * RUNNER_CIRCUMFERENCE;
  const pctLabel = Math.round(smooth * 100);
  const color =
    status === "failed"
      ? COLOR_FAILED
      : status === "pending"
        ? COLOR_PENDING
        : COLOR_PASSED;

  return (
    <div className={s.runnerProgressRingWrap}>
      <svg
        width={RUNNER_RING_SIZE}
        height={RUNNER_RING_SIZE}
        viewBox={`0 0 ${RUNNER_RING_SIZE} ${RUNNER_RING_SIZE}`}
        aria-hidden
      >
        <circle
          cx={RUNNER_RING_SIZE / 2}
          cy={RUNNER_RING_SIZE / 2}
          r={RUNNER_RADIUS}
          fill="none"
          stroke="rgba(156,163,175,0.25)"
          strokeWidth={RUNNER_STROKE}
        />
        {len > 0.01 ? (
          <circle
            cx={RUNNER_RING_SIZE / 2}
            cy={RUNNER_RING_SIZE / 2}
            r={RUNNER_RADIUS}
            fill="none"
            stroke={color}
            strokeWidth={RUNNER_STROKE}
            strokeDasharray={`${len} ${Math.max(0, RUNNER_CIRCUMFERENCE - len)}`}
            strokeDashoffset={0}
            strokeLinecap="round"
            transform={`rotate(-90 ${RUNNER_RING_SIZE / 2} ${RUNNER_RING_SIZE / 2})`}
          />
        ) : null}
      </svg>
      <span className={s.runnerProgressPct}>{pctLabel}%</span>
    </div>
  );
}

function RunnerDurationBar({ segments }: { segments: DurationSegment[] }) {
  const { t } = useI18n();
  const total = segments.reduce((sum, seg) => sum + seg.ms, 0);
  const [tooltip, setTooltip] = React.useState<{
    text: string;
    bottom: number;
    left: number;
  } | null>(null);

  if (total <= 0 || segments.length === 0) return null;

  return (
    <>
      <div className={s.runnerSegmentTrack} aria-label={t("pro.system.e2e.durationBreakdown")}>
        {segments.map((seg) => {
          const label = `${seg.label} ${formatDuration(seg.ms)}`;
          return (
            <div
              key={seg.id}
              className={s.runnerSegmentChip}
              style={{
                flexGrow: seg.ms,
                flexBasis: 0,
                background: seg.color,
              }}
              aria-label={label}
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setTooltip({
                  text: label,
                  bottom: window.innerHeight - rect.top + 8,
                  left: rect.left + rect.width / 2,
                });
              }}
              onMouseLeave={() => setTooltip(null)}
            />
          );
        })}
      </div>
      {typeof document !== "undefined" &&
        tooltip &&
        ReactDOM.createPortal(
          <div
            className={riStyles.tooltipPortal}
            style={{
              position: "fixed",
              bottom: tooltip.bottom,
              left: tooltip.left,
              transform: "translateX(-50%)",
              zIndex: 10000,
              pointerEvents: "none",
            }}
            role="tooltip"
          >
            <div
              className={riStyles.tooltipInner}
              style={{ position: "relative" }}
            >
              <span className={riStyles.tooltipTitle}>{tooltip.text}</span>
              <div className={riStyles.tooltipCaret} />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function countSpecsByProject(report: E2eHealthReport | null): Map<string, number> {
  const map = new Map<string, number>();
  for (const spec of report?.specs ?? []) {
    map.set(spec.project, (map.get(spec.project) ?? 0) + 1);
  }
  return map;
}

function resolveRunnerProjects(
  activeRun: E2eRun | null,
  selected: Set<E2ePlaywrightProject>,
): E2ePlaywrightProject[] {
  // Always render in Playwright config order (smoke → authenticated → onboarding).
  const runProjects = activeRun?.projects ?? [];
  const wanted =
    activeRun && runProjects.length === 0
      ? new Set<string>(E2E_PROJECTS)
      : runProjects.length > 0
        ? new Set(runProjects)
        : selected;
  return E2E_PROJECTS.filter((p) => wanted.has(p));
}

function buildRunnerProgressCards(
  projects: E2ePlaywrightProject[],
  activeRun: E2eRun | null,
  report: E2eHealthReport | null,
): Array<{
  name: string;
  progress: number;
  status: E2eRunProjectSegment["status"];
}> {
  const byName = new Map(
    (activeRun?.segments?.projects ?? []).map((p) => [p.name, p] as const),
  );
  const reportCounts = countSpecsByProject(report);

  const prepareFromServer =
    typeof activeRun?.prepareProgress === "number"
      ? activeRun.prepareProgress
      : null;
  const prepareElapsed = activeRun?.startedAt
    ? Math.max(0, Date.now() - new Date(activeRun.startedAt).getTime())
    : 0;
  const prepareFallback = Math.min(0.95, prepareElapsed / PREPARE_ESTIMATE_MS);
  const phase = activeRun?.phaseLabel ?? "";
  const prepareDone =
    phase.startsWith("Project:") ||
    phase === "Running Playwright" ||
    phase === "Publishing Results" ||
    phase === "Completing" ||
    phase === "Stopped" ||
    (prepareFromServer != null && prepareFromServer >= 1);
  // Never let prepare % go backwards: server phase floors can be below the
  // local elapsed estimate (e.g. timer 40% then PROVISIONING reported as 30%).
  const prepareProgress = prepareDone
    ? 1
    : Math.max(prepareFromServer ?? 0, prepareFallback);

  const cards: Array<{
    name: string;
    progress: number;
    status: E2eRunProjectSegment["status"];
  }> = [
    {
      name: "prepare",
      progress: prepareProgress,
      status: prepareDone ? "passed" : "running",
    },
  ];

  for (const name of projects) {
    const seg = byName.get(name);
    const total =
      (typeof seg?.totalSpecs === "number" && seg.totalSpecs > 0
        ? seg.totalSpecs
        : reportCounts.get(name)) ?? 0;
    const currentIndex =
      typeof seg?.currentIndex === "number"
        ? seg.currentIndex
        : typeof seg?.completedSpecs === "number"
          ? seg.completedSpecs
          : 0;
    const progress = total > 0 ? Math.min(1, currentIndex / total) : 0;
    const status =
      seg?.status ??
      (activeRun?.currentProject === name ? "running" : "pending");
    cards.push({ name, progress, status });
  }

  return cards;
}

function runnerRingLabel(name: string): string {
  if (name === "prepare") return "prepare";
  return name;
}

function statusBadgeColor(status: E2eSpecResult["status"]): string | undefined {
  if (status === "passed") return COLOR_PASSED;
  if (status === "failed" || status === "timedOut" || status === "interrupted") {
    return COLOR_FAILED;
  }
  // Skipped segment is white on the bar; use a clear slate for readable chips.
  if (status === "skipped") return "#64748B";
  return undefined;
}

function statusBadgeIcon(status: E2eSpecResult["status"]): React.ReactNode {
  const size = 12;
  if (status === "passed") return <Check size={size} strokeWidth={2.5} aria-hidden />;
  if (status === "failed" || status === "timedOut" || status === "interrupted") {
    return <X size={size} strokeWidth={2.5} aria-hidden />;
  }
  if (status === "skipped") return <Minus size={size} strokeWidth={2.5} aria-hidden />;
  return null;
}

function useElapsedLabel(startIso: string | null | undefined): string {
  const [display, setDisplay] = React.useState("0s");
  React.useEffect(() => {
    if (!startIso) {
      setDisplay("0s");
      return;
    }
    const update = () => {
      const elapsed = Date.now() - new Date(startIso).getTime();
      setDisplay(formatDuration(Math.max(0, elapsed)));
    };
    update();
    const id = window.setInterval(update, 1000);
    return () => window.clearInterval(id);
  }, [startIso]);
  return display;
}

function ProjectCheck({ checked }: { checked: boolean }) {
  return (
    <span className={clsx(s.checkbox, checked && s.checkboxChecked)} aria-hidden>
      {checked ? <Check size={10} strokeWidth={3} /> : null}
    </span>
  );
}

function RunSuiteControl({
  disabled,
  running,
  selected,
  onChange,
  onRun,
}: {
  disabled?: boolean;
  running: boolean;
  selected: Set<E2ePlaywrightProject>;
  onChange: (next: Set<E2ePlaywrightProject>) => void;
  onRun: () => void;
}) {
  const { t } = useI18n();
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const allSelected = E2E_PROJECTS.every((p) => selected.has(p));
  const shortLabel = allSelected
    ? "All"
    : selected.size === 0
      ? "None"
      : selected.size === 1
        ? [...selected][0]
        : `${selected.size}`;

  React.useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (!dropdownRef.current?.contains(e.target as Node)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  const toggleAll = () => {
    if (allSelected) onChange(new Set());
    else onChange(new Set(E2E_PROJECTS));
  };

  const toggleOne = (project: E2ePlaywrightProject) => {
    const next = new Set(selected);
    if (next.has(project)) next.delete(project);
    else next.add(project);
    onChange(next);
  };

  const busy = running || disabled;

  return (
    <div style={{ position: "relative" }} ref={dropdownRef}>
      <div
        style={{
          display: "flex",
          borderRadius: 6,
          overflow: "hidden",
          border: "1px solid rgba(0,0,0,0.15)",
          height: 34,
          opacity: busy ? 0.55 : 1,
          pointerEvents: busy ? "none" : "auto",
        }}
      >
        <button
          type="button"
          disabled={busy || selected.size === 0}
          onClick={onRun}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "0 12px",
            height: "100%",
            background: "#FFCF51",
            border: "none",
            borderRight: "1px solid rgba(0,0,0,0.15)",
            cursor: busy || selected.size === 0 ? "not-allowed" : "pointer",
            fontSize: 13,
            fontWeight: 600,
            color: "#111827",
            whiteSpace: "nowrap",
          }}
        >
          {running ? (
            <span
              style={{
                display: "inline-block",
                width: 14,
                height: 14,
                border: "2px solid #111827",
                borderTopColor: "transparent",
                borderRadius: "50%",
                animation: "spin 0.6s linear infinite",
              }}
            />
          ) : (
            <Play size={13} strokeWidth={2.5} />
          )}
          {running ? "Running…" : "Run suite"}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setDropdownOpen((p) => !p)}
          aria-label={t("pro.system.e2e.selectPlaywrightProjects")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "0 8px",
            height: "100%",
            background: "#FFCF51",
            border: "none",
            cursor: busy ? "not-allowed" : "pointer",
            fontSize: 13,
            fontWeight: 600,
            color: "#111827",
            whiteSpace: "nowrap",
            maxWidth: 120,
          }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {shortLabel}
          </span>
          <ChevronDown size={13} strokeWidth={2.5} />
        </button>
      </div>
      {dropdownOpen && !busy ? (
        <div className={s.projectMenu} role="menu">
          <button
            type="button"
            className={s.projectMenuItem}
            style={{ fontWeight: allSelected ? 600 : 500 }}
            onClick={toggleAll}
          >
            <ProjectCheck checked={allSelected} />
            All projects
          </button>
          <div className={s.projectMenuDivider} />
          {E2E_PROJECTS.map((project) => (
            <button
              key={project}
              type="button"
              className={s.projectMenuItem}
              style={{ fontWeight: selected.has(project) ? 600 : 400 }}
              onClick={() => toggleOne(project)}
            >
              <ProjectCheck checked={selected.has(project)} />
              {project}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function E2eTestingPage() {
  const { t } = useI18n();
  const { accessToken, user } = useAuth();
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse(SCOPE);
  const { subscribe, unsubscribe } = useWebSocket();

  const [report, setReport] = React.useState<E2eHealthReport | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [selectedProjects, setSelectedProjects] = React.useState<Set<E2ePlaywrightProject>>(
    () => new Set(E2E_PROJECTS),
  );
  const [activeRun, setActiveRun] = React.useState<E2eRun | null>(null);
  const [kickoffError, setKickoffError] = React.useState<string | null>(null);
  const [starting, setStarting] = React.useState(false);
  const [stopping, setStopping] = React.useState(false);

  const load = React.useCallback(async (isRefresh = false) => {
    const token = accessToken ?? getAuthCookie("access_token");
    if (!token) {
      setLoadError("Not signed in");
      setLoading(false);
      return;
    }
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchE2eHealth(token, { environment: "dev" });
      setReport(data);
      if (data.error) setLoadError(data.error);
      if (isRefresh) setRefreshKey((k) => k + 1);
    } catch (err: unknown) {
      setLoadError(err instanceof Error ? err.message : "Failed to load E2E health");
      setReport(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [accessToken]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    const handler = (msg: { payload?: unknown }) => {
      const payload = msg.payload as E2eRun | undefined;
      if (!payload?.runId) return;
      setActiveRun((prev) => {
        if (prev && prev.runId !== payload.runId && isActiveRunStatus(prev.status)) {
          return prev;
        }
        // Ignore late simulate ticks that try to revive a finished/stopped run.
        if (
          prev?.runId === payload.runId &&
          isTerminalRunStatus(prev.status) &&
          isActiveRunStatus(payload.status)
        ) {
          return prev;
        }
        return payload;
      });
      if (isTerminalRunStatus(payload.status)) {
        void load(true);
      }
    };
    subscribe(["e2e:runs:*"], handler);
    return () => unsubscribe(["e2e:runs:*"], handler);
  }, [subscribe, unsubscribe, load]);

  const running = starting || isActiveRunStatus(activeRun?.status);
  const elapsedLabel = useElapsedLabel(running ? activeRun?.startedAt : null);

  const onKickoff = React.useCallback(async () => {
    const token = accessToken ?? getAuthCookie("access_token");
    if (!token) {
      setKickoffError("Not signed in");
      return;
    }
    if (selectedProjects.size === 0) {
      setKickoffError("Select at least one Playwright project");
      return;
    }
    setKickoffError(null);
    setStarting(true);
    try {
      const all = E2E_PROJECTS.every((p) => selectedProjects.has(p));
      const projects = all ? undefined : ([...selectedProjects] as E2ePlaywrightProject[]);
      const res = await startE2eRun(token, {
        environment: "dev",
        projects,
      });
      setActiveRun({
        runId: res.runId,
        environment: res.environment,
        status: res.status,
        pipelineExecutionId: res.pipelineExecutionId,
        codeBuildId: res.codeBuildId,
        projects: res.projects?.length
          ? res.projects
          : all
            ? [...E2E_PROJECTS]
            : [...selectedProjects],
        message: res.message,
        phaseLabel: res.phaseLabel,
        stepLabel: res.stepLabel,
        startedAt: res.startedAt ?? new Date().toISOString(),
      });
    } catch (err: unknown) {
      setKickoffError(err instanceof Error ? err.message : "Failed to start E2E run");
    } finally {
      setStarting(false);
    }
  }, [accessToken, selectedProjects]);

  const onStop = React.useCallback(async () => {
    const token = accessToken ?? getAuthCookie("access_token");
    if (!token || !activeRun?.runId) return;
    const runId = activeRun.runId;
    setKickoffError(null);
    setStopping(true);
    try {
      const stopped = await stopE2eRun(token, runId);
      setActiveRun(stopped);
      void load(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to stop E2E run";
      // After restart / race, run may already be gone or finished server-side.
      const status = err instanceof ProApiClientError ? err.status : undefined;
      const stale =
        status === 404 ||
        status === 409 ||
        /not found|not in progress/i.test(message);
      if (stale) {
        setActiveRun((prev) =>
          prev?.runId === runId
            ? {
                ...prev,
                status: "stopped",
                message: "Stopped",
                phase: "Stopped",
                finishedAt: prev.finishedAt ?? new Date().toISOString(),
              }
            : prev,
        );
        void load(true);
        setKickoffError(null);
      } else {
        setKickoffError(message);
      }
    } finally {
      setStopping(false);
    }
  }, [accessToken, activeRun?.runId, load]);

  const sectionLayout = bindSectionLayout([
    { id: SECTION_HEALTH, label: "E2E Health", icon: <HeartPulse size={14} /> },
    { id: SECTION_RUNNER, label: "E2E Runner", icon: <Rocket size={14} /> },
    { id: SECTION_REPORT, label: "E2E Report", icon: <FileText size={14} /> },
  ]);

  const specs = report?.specs ?? [];
  const githubRunUrl = safeHttpUrl(report?.github?.runUrl);
  const projectTotals = React.useMemo(() => aggregateByProject(specs), [specs]);

  const specRows = React.useMemo<SpecRow[]>(
    () =>
      specs.map((spec, index) => ({
        ...spec,
        id: `${spec.project}:${spec.file}:${spec.title}:${index}`,
      })),
    [specs],
  );

  const projectFilterOptions = React.useMemo(
    () =>
      [...new Set(specs.map((r) => r.project).filter(Boolean))]
        .sort()
        .map((value) => ({ value, label: value })),
    [specs],
  );

  const columns = React.useMemo<ColumnDefinition<SpecRow>[]>(
    () => [
      {
        id: "title",
        label: "Spec",
        sortable: true,
        pinnedLeft: true,
        render: (row) => row.title,
      },
      {
        id: "status",
        label: "Status",
        sortable: true,
        filterType: "checkbox",
        filterOptions: [
          { value: "passed", label: "Passed" },
          { value: "failed", label: "Failed" },
          { value: "skipped", label: "Skipped" },
          { value: "timedOut", label: "Timed out" },
          { value: "interrupted", label: "Interrupted" },
        ],
        render: (row) => row.status,
      },
      {
        id: "project",
        label: "Project",
        sortable: true,
        filterType: "checkbox",
        filterOptions: projectFilterOptions,
        render: (row) => row.project,
      },
      {
        id: "file",
        label: "File",
        sortable: true,
        render: (row) => (
          <span style={{ fontFamily: "monospace", fontSize: 12 }}>{row.file}</span>
        ),
      },
      {
        id: "durationMs",
        label: "Duration",
        sortable: true,
        render: (row) => formatDuration(row.durationMs),
      },
      {
        id: "error",
        label: "Error",
        sortable: false,
        render: (row) =>
          row.error ? (
            <span style={{ color: "#DC2626", wordBreak: "break-word" }}>{row.error}</span>
          ) : (
            "—"
          ),
      },
    ],
    [projectFilterOptions],
  );

  const fetchSpecs = React.useCallback(
    async (params: ListParams) => {
      let filtered = specRows;

      const q = (params.search ?? "").trim().toLowerCase();
      if (q) {
        filtered = filtered.filter(
          (row) =>
            row.title.toLowerCase().includes(q) ||
            row.file.toLowerCase().includes(q) ||
            row.project.toLowerCase().includes(q) ||
            row.status.toLowerCase().includes(q) ||
            (row.error ?? "").toLowerCase().includes(q),
        );
      }

      const projects = asStringArray(params.filters?.project);
      if (projects.length > 0) {
        const set = new Set(projects);
        filtered = filtered.filter((row) => set.has(row.project));
      }

      const statuses = asStringArray(params.filters?.status);
      if (statuses.length > 0) {
        const set = new Set(statuses);
        filtered = filtered.filter((row) => set.has(row.status));
      }

      const sortBy = params.sort_by || "title";
      const dir = params.order === "desc" ? -1 : 1;
      filtered = [...filtered].sort((a, b) => {
        if (sortBy === "durationMs") {
          return (a.durationMs - b.durationMs) * dir;
        }
        const key = sortBy as keyof SpecRow;
        const av = String(a[key] ?? "").toLowerCase();
        const bv = String(b[key] ?? "").toLowerCase();
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });

      const page = Math.max(1, params.page || 1);
      const limit = Math.max(1, params.limit || 10);
      const start = (page - 1) * limit;
      return {
        data: filtered.slice(start, start + limit),
        total: filtered.length,
      };
    },
    [specRows],
  );

  const showMockBanner =
    report?.source === "fixture" ||
    (import.meta.env.DEV && Boolean(report) && report?.source !== "s3");

  const passed = report?.totals.passed ?? 0;
  const failed = report?.totals.failed ?? 0;
  const skipped = report?.totals.skipped ?? 0;
  const flaky = report?.totals.flaky ?? 0;
  const counted = Math.max(passed + failed + skipped, 1);
  const passedPct = pct(passed, counted);
  const failedPct = pct(failed, counted);
  const skippedPct = pct(skipped, counted);

  const lastRunBadge =
    report && !report.empty ? (
      <LastRunBadge report={report} runUrl={githubRunUrl} />
    ) : null;

  const lastRunPassed =
    report?.conclusion === "passed" ||
    (report && !report.empty && (report.totals.failed ?? 0) === 0 && (report.totals.passed ?? 0) > 0);
  const lastRunFailed =
    report?.conclusion === "failed" || (report && (report.totals.failed ?? 0) > 0);

  const runnerPhase =
    activeRun?.phaseLabel ||
    (activeRun?.message?.includes("—")
      ? activeRun.message.split("—")[0]?.trim()
      : null) ||
    "Starting…";
  const runnerStep =
    activeRun?.stepLabel ||
    (activeRun?.message?.includes("—")
      ? activeRun.message.split("—").slice(1).join("—").trim()
      : activeRun?.message) ||
    (activeRun?.phase ? `Pipeline: ${activeRun.phase}` : null) ||
    null;

  const runnerProjects = resolveRunnerProjects(activeRun, selectedProjects);
  const runnerProgressCards = buildRunnerProgressCards(
    runnerProjects,
    activeRun,
    report,
  );

  return (
    <DetailPageLayout
      header={{
        media: <FlaskConical size={48} />,
        title: "E2E Testing",
        subtitle: "Playwright health for pro-dev (CI summaries + kickoff)",
      }}
      contextActions={[
        {
          id: "refresh",
          label: refreshing ? "Refreshing…" : "Refresh",
          icon: <RefreshCw size={14} />,
          onClick: () => void load(true),
          disabled: refreshing || loading,
        },
      ]}
      {...sectionLayout}
    >
      {showMockBanner ? (
        <div className={s.pageBanner}>
          <Callout variant="info">
            Mocked local data for UI development. GitHub links and pass/fail totals
            come from a fixture file, not a live CI run. Live summaries are on
            pro-dev after Playwright publishes to S3. Local kickoff uses simulate
            mode (progress only; fixture is unchanged).
          </Callout>
        </div>
      ) : null}

      <CollapsibleSection
        id={SECTION_HEALTH}
        title={t("pro.system.e2e.health")}
        icon={<HeartPulse size={20} />}
        expanded={isExpanded(SECTION_HEALTH)}
        onToggleExpanded={() => toggle(SECTION_HEALTH)}
        statusIndicators={lastRunBadge ? [lastRunBadge] : undefined}
      >
        <div className={s.sectionInner}>
          {loadError && <Callout variant="error">{loadError}</Callout>}
          {loading && !report ? (
            <div style={{ height: 80 }}>
              <AuthTransitionScreen message="Loading…" variant="compact" />
            </div>
          ) : report?.empty ? (
            <Callout variant="info">
              No E2E summary published yet for this environment. After the next
              Playwright push workflow on <code>dev</code>, or a kickoff from this
              page, results appear here.
            </Callout>
          ) : report ? (
            <>
              <div
                className={s.barTrack}
                role="img"
                aria-label={`Passed ${passed}, failed ${failed}, skipped ${skipped}`}
              >
                {passed > 0 ? (
                  <div
                    className={s.barSegPassed}
                    style={{
                      flex:
                        failed === 0 && skipped === 0
                          ? "1 1 0"
                          : `${passedPct} 0 0`,
                    }}
                    title={`Passed: ${passed}`}
                  />
                ) : null}
                {failed > 0 ? (
                  <div
                    className={s.barSegFailed}
                    style={{ flex: `${failedPct} 0 0` }}
                    title={`Failed: ${failed}`}
                  />
                ) : null}
                {skipped > 0 ? (
                  <div
                    className={s.barSegSkipped}
                    style={{ flex: `${skippedPct} 0 0` }}
                    title={`Skipped: ${skipped}`}
                  />
                ) : null}
                <span className={s.barPct} aria-hidden>
                  {formatPct(passed, passed + failed + skipped)}
                </span>
              </div>

              <div className={s.legend}>
                <span className={s.legendItem}>
                  <span className={clsx(s.swatch, s.swatchPassed)} />
                  Passed: {passed}
                </span>
                <span className={s.legendItem}>
                  <span className={clsx(s.swatch, s.swatchFailed)} />
                  Failed: {failed}
                </span>
                <span className={s.legendItem}>
                  <span className={clsx(s.swatch, s.swatchSkipped)} />
                  Skipped: {skipped}
                </span>
                {flaky > 0 ? (
                  <span className={s.legendItem}>Flaky: {flaky}</span>
                ) : null}
                <span className={s.legendItem}>
                  Total: {passed + failed + skipped}
                </span>
              </div>

              {projectTotals.length > 0 ? (
                <div className={s.ringsRow}>
                  {projectTotals.map((proj) => (
                    <div key={proj.project} className={s.ringCard}>
                      <ProjectRing
                        passed={proj.passed}
                        failed={proj.failed}
                        skipped={proj.skipped}
                        total={proj.total}
                      />
                      <div className={s.ringLabel}>{proj.project}</div>
                      <div className={s.ringCounts}>
                        {proj.passed} / {proj.failed} / {proj.skipped}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id={SECTION_RUNNER}
        title={t("pro.system.e2e.runner")}
        icon={<Rocket size={20} />}
        expanded={isExpanded(SECTION_RUNNER)}
        onToggleExpanded={() => toggle(SECTION_RUNNER)}
        highlight={running ? "success" : "info"}
        statusIndicators={[
          <div
            key="runner-actions"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              minWidth: 168,
              minHeight: 34,
            }}
          >
            {running ? (
              <Button
                size="sm"
                color="danger"
                variant="outline"
                disabled={stopping || !activeRun?.runId}
                loading={stopping}
                iconLeft={<Square size={12} />}
                onClick={() => void onStop()}
              >
                {stopping ? "Stopping…" : "Stop"}
              </Button>
            ) : (
              <RunSuiteControl
                running={running}
                disabled={loading}
                selected={selectedProjects}
                onChange={setSelectedProjects}
                onRun={() => void onKickoff()}
              />
            )}
          </div>,
        ]}
      >
        <div className={s.sectionInner}>
          {kickoffError ? <Callout variant="error">{kickoffError}</Callout> : null}
          {running ? (
            <div className={s.runnerActiveBox} aria-live="polite">
              <div className={s.runnerProgressRow}>
                {runnerProgressCards.map((card) => (
                  <div key={card.name} className={s.runnerProgressCard}>
                    <RunnerProgressRing
                      progress={card.progress}
                      status={card.status}
                    />
                    <span className={s.runnerProgressLabel}>
                      {runnerRingLabel(card.name)}
                    </span>
                  </div>
                ))}
              </div>
              <div className={s.runnerActiveMeta}>
                <span className={s.runnerActiveElapsed}>{elapsedLabel}</span>
                <span className={s.runnerPhaseLabel}>{runnerPhase}</span>
                {runnerStep ? (
                  <span className={s.runnerStepLabel}>{runnerStep}</span>
                ) : null}
              </div>
            </div>
          ) : loading && !report ? (
            <div className={s.runnerIdleEmpty}>
              <AuthTransitionScreen message="Loading…" variant="compact" />
            </div>
          ) : report && !report.empty ? (
            <div className={s.runnerIdleBox}>
              <span
                className={clsx(
                  s.runnerIdleVerdictIcon,
                  lastRunPassed
                    ? s.runnerIdleVerdictPassed
                    : lastRunFailed
                      ? s.runnerIdleVerdictFailed
                      : s.runnerIdleVerdictUnknown,
                )}
                aria-hidden
              >
                {lastRunPassed ? (
                  <Check size={28} strokeWidth={2.5} />
                ) : lastRunFailed ? (
                  <X size={28} strokeWidth={2.5} />
                ) : (
                  <Minus size={28} strokeWidth={2.5} />
                )}
              </span>
              <div className={s.runnerIdleCopy}>
                <span className={s.runnerIdleHeadline}>
                  Last run {formatRelativeAgo(report.generatedAt)}
                </span>
                <span className={s.runnerIdleWhen}>
                  {formatWhen(report.generatedAt)}
                </span>
                {report.durationMs > 0 ? (
                  <span className={s.runnerIdleDuration}>
                    {formatDuration(report.durationMs)}
                  </span>
                ) : null}
                <RunnerDurationBar segments={buildDurationSegments(report)} />
              </div>
            </div>
          ) : loadError ? (
            <div className={s.runnerIdleEmpty}>
              <Text level="body" color="muted">
                Couldn’t load the last run.
              </Text>
            </div>
          ) : (
            <div className={s.runnerIdleEmpty}>
              <Text level="body" color="muted">
                No completed run yet. Choose projects and click Run suite.
              </Text>
            </div>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        id={SECTION_REPORT}
        title={t("pro.system.e2e.report")}
        icon={<FileText size={20} />}
        expanded={isExpanded(SECTION_REPORT)}
        onToggleExpanded={() => toggle(SECTION_REPORT)}
      >
        <div className={s.sectionInner}>
          {loading && !report ? (
            <div style={{ height: 80 }}>
              <AuthTransitionScreen message="Loading…" variant="compact" />
            </div>
          ) : !specRows.length ? (
            <Callout variant="info">{t("pro.system.e2e.noSpecResults")}</Callout>
          ) : (
            <ListView<SpecRow>
              listId="system-e2e-report"
              userId={user?.id ?? "anonymous"}
              columns={columns}
              fetchList={fetchSpecs}
              contextKey={`${refreshKey}:${specRows.length}`}
              enabledViews={["stack", "table"]}
              compact
              defaultPageSize={10}
              disableLocalPersistence
              searchPlaceholder="Search specs…"
              emptyTitle="No matching specs"
              emptySubtitle={t("pro.system.e2e.tryClearingFilters")}
              defaultSort={{ sort_by: "title", order: "asc" }}
              renderStackCard={(row, ctx) => (
                <ListViewCard
                  id={row.id}
                  layout="stack"
                  imageFormat="none"
                  compact={ctx.compact}
                  title={row.title}
                  subtitle={row.error || undefined}
                  badges={[
                    {
                      id: "status",
                      label: row.status,
                      color: statusBadgeColor(row.status),
                      icon: statusBadgeIcon(row.status),
                    },
                    { id: "project", label: row.project },
                    {
                      id: "file",
                      label: formatSpecPathBadge(row.file),
                      title: row.file,
                    },
                    { id: "duration", label: formatDuration(row.durationMs) },
                  ]}
                />
              )}
            />
          )}
        </div>
      </CollapsibleSection>
    </DetailPageLayout>
  );
}
